// src/components/organisation/renderers/LeadershipSectionRenderer.jsx
import React from 'react';
import { Mail, Phone, Linkedin, Twitter, MapPin } from 'lucide-react';

export const LeadershipSectionRenderer = ({ 
  data, 
  bookData, 
  customStyling = {}, 
  onInteraction 
}) => {
  const {
    heading = 'Our Leadership',
    subHeading = '',
    members = [],
    departments = [],
    enabled = true
  } = data || {};

  if (!enabled) return null;

  const trackInteraction = (action, details = {}) => {
    if (onInteraction) {
      onInteraction({
        type: 'leadership_interaction',
        action,
        section: 'leadership',
        sectionId: data?.id,
        ...details
      });
    }
  };

  const handleContactClick = (member, contactType, value) => {
    trackInteraction('contact_click', { memberId: member.name, contactType, value });
    
    if (contactType === 'email') {
      window.location.href = `mailto:${value}`;
    } else if (contactType === 'phone') {
      window.location.href = `tel:${value}`;
    } else if (contactType === 'linkedin') {
      window.open(value, '_blank', 'noopener,noreferrer');
    } else if (contactType === 'twitter') {
      window.open(value, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <section className="leadership-section py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section header */}
        {(heading || subHeading) && (
          <div className="text-center mb-16">
            {heading && (
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {heading}
              </h2>
            )}
            {subHeading && (
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {subHeading}
              </p>
            )}
          </div>
        )}

        {/* Team members */}
        {members?.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {members.map((member, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                {/* Member photo */}
                {member.image && (
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                {/* Member info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {member.name}
                  </h3>
                  {member.position && (
                    <p className="text-blue-600 font-medium mb-3">
                      {member.position}
                    </p>
                  )}
                  {member.bio && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {member.bio}
                    </p>
                  )}
                  
                  {/* Contact info */}
                  <div className="flex flex-wrap gap-3">
                    {member.email && (
                      <button
                        onClick={() => handleContactClick(member, 'email', member.email)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <Mail className="h-4 w-4" />
                      </button>
                    )}
                    {member.phone && (
                      <button
                        onClick={() => handleContactClick(member, 'phone', member.phone)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <Phone className="h-4 w-4" />
                      </button>
                    )}
                    {member.linkedin && (
                      <button
                        onClick={() => handleContactClick(member, 'linkedin', member.linkedin)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <Linkedin className="h-4 w-4" />
                      </button>
                    )}
                    {member.twitter && (
                      <button
                        onClick={() => handleContactClick(member, 'twitter', member.twitter)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <Twitter className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Departments */}
        {departments?.length > 0 && (
          <div>
            <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Our Departments
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {departments.map((department, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                  <h4 className="text-xl font-bold text-gray-900 mb-2">
                    {department.name}
                  </h4>
                  {department.head && (
                    <p className="text-blue-600 font-medium mb-3">
                      Head: {department.head}
                    </p>
                  )}
                  {department.description && (
                    <p className="text-gray-600">
                      {department.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};